import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { ProfileQuestionnaireFields } from '../components/ProfileQuestionnaireFields';
import { emptyProfileForm, profilePayload, validateSignupForm } from '../lib/onboarding';
import { Eye, EyeOff, AlertCircle, Loader2, CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SignupPage() {
  const [form, setForm] = useState({ ...emptyProfileForm(), email: '', password: '', confirmPassword: '' });
  const [fieldErrors, setFieldErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [preflight, setPreflight] = useState({ loading: true, ready: false, error: '' });
  const { signUp } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    let active = true;
    fetch(`${API_URL}/api/onboarding/preflight`)
      .then(async response => {
        const body = await response.json().catch(() => ({}));
        if (!response.ok || body.ready !== true) throw new Error('Signup is temporarily unavailable.');
        return body;
      })
      .then(() => active && setPreflight({ loading: false, ready: true, error: '' }))
      .catch(() => active && setPreflight({
        loading: false,
        ready: false,
        error: 'Signup is temporarily unavailable while profile storage is checked.',
      }));
    return () => { active = false; };
  }, []);

  const setField = (name, value) => {
    setForm(current => ({ ...current, [name]: value }));
    setFieldErrors(current => ({ ...current, [name]: undefined }));
  };

  const handleSubmit = async event => {
    event.preventDefault();
    setError('');
    const nextErrors = validateSignupForm(form);
    setFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length || !preflight.ready) return;
    setLoading(true);
    try {
      const { error: signupError } = await signUp({
        ...profilePayload(form),
        email: form.email.trim(),
        password: form.password,
        confirm_password: form.confirmPassword,
      });
      if (signupError) setError(signupError.message);
      else {
        setSuccess(true);
        setTimeout(() => navigate('/verify-email'), 2000);
      }
    } catch (_error) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="w-full max-w-md">
          <div className="card-elevated p-8 text-center" data-testid="signup-success-panel">
            <div className="mx-auto w-16 h-16 bg-[hsl(var(--success))] rounded-full flex items-center justify-center mb-4">
              <CheckCircle2 className="h-8 w-8 text-white" />
            </div>
            <h2 className="text-2xl font-semibold text-foreground mb-2">Check Your Email</h2>
            <p className="text-muted-foreground" data-testid="signup-success-message">
              We've sent a verification link to <strong>{form.email.trim()}</strong>.
              Please check your inbox and click the link to verify your account.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const formDisabled = loading || !preflight.ready;

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-12 relative overflow-hidden">
      <div className="absolute inset-0 z-0">
        <img src="https://customer-assets.emergentagent.com/job_ifc-validation-tool/artifacts/blnbj8b4_ChatGPT%20Image%20Mar%2026%2C%202026%2C%2010_47_14%20PM.png"
          alt="BIM Technology" className="w-full h-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/60 to-background/80" />
      </div>
      <div className="w-full max-w-3xl relative z-10">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-foreground mb-2"><span className="text-primary">AHA</span></h1>
          <p className="text-sm text-muted-foreground">Autonomous AI-Enabled Handover Agent</p>
        </div>

        <div className="card-elevated p-5 sm:p-8 bg-card/95 backdrop-blur-sm">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-foreground">Create Account</h2>
            <p className="text-sm text-muted-foreground mt-1">Create your AHA contact profile and account</p>
          </div>

          {preflight.loading && (
            <Alert className="mb-6" data-testid="signup-preflight-loading">
              <Loader2 className="h-4 w-4 animate-spin" />
              <AlertDescription>Checking secure signup availability…</AlertDescription>
            </Alert>
          )}
          {preflight.error && (
            <Alert variant="destructive" className="mb-6" data-testid="signup-preflight-error">
              <AlertCircle className="h-4 w-4" /><AlertDescription>{preflight.error}</AlertDescription>
            </Alert>
          )}
          {error && (
            <Alert variant="destructive" className="mb-6" data-testid="signup-form-error">
              <AlertCircle className="h-4 w-4" /><AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="signup-form">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" placeholder="name@company.com" value={form.email}
                onChange={event => setField('email', event.target.value)} autoComplete="email"
                aria-invalid={Boolean(fieldErrors.email)} aria-describedby="signup-email-error"
                required disabled={formDisabled} data-testid="signup-email-input" />
              {fieldErrors.email && <p id="signup-email-error" role="alert" className="text-xs text-destructive" data-testid="signup-email-error">{fieldErrors.email}</p>}
            </div>

            <ProfileQuestionnaireFields form={form} errors={fieldErrors} setField={setField} disabled={formDisabled} />

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input id="password" type={showPassword ? 'text' : 'password'} placeholder="Minimum 8 characters"
                    value={form.password} onChange={event => setField('password', event.target.value)}
                    autoComplete="new-password" aria-invalid={Boolean(fieldErrors.password)}
                    aria-describedby="signup-password-error" required disabled={formDisabled}
                    data-testid="signup-password-input" />
                  <button type="button" onClick={() => setShowPassword(value => !value)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    disabled={formDisabled} aria-label={showPassword ? 'Hide password' : 'Show password'}
                    data-testid="signup-password-visibility-button">
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {fieldErrors.password && <p id="signup-password-error" role="alert" className="text-xs text-destructive" data-testid="signup-password-error">{fieldErrors.password}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm password</Label>
                <Input id="confirmPassword" type="password" placeholder="Re-enter your password"
                  value={form.confirmPassword} onChange={event => setField('confirmPassword', event.target.value)}
                  autoComplete="new-password" aria-invalid={Boolean(fieldErrors.confirmPassword)}
                  aria-describedby="signup-confirm-password-error" required disabled={formDisabled}
                  data-testid="signup-confirm-password-input" />
                {fieldErrors.confirmPassword && <p id="signup-confirm-password-error" role="alert" className="text-xs text-destructive" data-testid="signup-confirm-password-error">{fieldErrors.confirmPassword}</p>}
              </div>
            </div>

            <Button type="submit" className="w-full" disabled={formDisabled} data-testid="signup-submit-button">
              {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Creating account…</> : 'Sign Up'}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm">
            <span className="text-muted-foreground">Already have an account? </span>
            <Link to="/login" className="text-primary hover:underline font-medium" data-testid="signup-login-link">Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}